/**
 * User Profile Utility Functions
 * Centralized utility functions for user profile operations
 */

import { toast } from "react-toastify";
import { getToastConfig, toastConfigs } from "./toastConfig";
import { logout, reset } from "../../../../../features/auth/authSlice";
import { reset as invoiceReset } from "../../../../../features/invoice/invoiceSlice";
import { reset as headerReset } from "../../../../../features/headerText/headerTextSlice";
import { reset as appSettingsReset } from "../../../../../features/appSettings/appSettingsSlice";

/**
 * Handle deleted user scenario
 * Centralized function to handle when a user is deleted
 * @param {Function} dispatch - Redux dispatch function
 * @param {Function} navigate - React Router navigate function
 * @param {string} redirectPath - Optional custom redirect path (default: "/")
 */
export const handleDeletedUser = (dispatch, navigate, redirectPath = "/") => {
  // Remove user from local storage
  localStorage.removeItem("user");

  // Dispatch logout and reset actions
  dispatch(logout());
  dispatch(reset());
  dispatch(invoiceReset());
  dispatch(headerReset());
  dispatch(appSettingsReset());

  // Show toast notification
  toast.error("This user is deleted!", toastConfigs.short);

  // Redirect to specified path
  navigate(redirectPath);
};

/**
 * Check if error indicates deleted user
 * @param {Object} error - Error object from API response
 * @returns {boolean} True if user is deleted
 */
export const isDeletedUserError = (error) => {
  return (
    error?.response?.data?.status === "deleted" ||
    error?.response?.data?.status === "userDeleted"
  );
};

/**
 * Handle API error with deleted user check
 * @param {Object} error - Error object from API response
 * @param {Function} dispatch - Redux dispatch function
 * @param {Function} navigate - React Router navigate function
 * @param {string} redirectPath - Optional custom redirect path
 * @returns {boolean} True if deleted user was handled
 */
export const handleApiError = (error, dispatch, navigate, redirectPath = "/") => {
  if (isDeletedUserError(error)) {
    if (error?.response?.data?.status === "userDeleted") {
      handleDeletedUser(dispatch, navigate, "/user/myteam/list");
    } else {
      handleDeletedUser(dispatch, navigate, redirectPath);
    }
    return true;
  }
  return false;
};
